import { loader } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { AnimatedText, EVENTS, Grid, SmallButton } from "@folio-2026/shared"
import { useGSAP } from "@gsap/react"
import { gsap, ScrollTrigger } from "gsap/all"
import Lenis from "lenis"
import { useCallback, useEffect, useRef } from "react"

import useSectionReady from "hooks/useSectionReady"

gsap.registerPlugin(ScrollTrigger)

export default function AboutContent() {
  const isSectionReady = useSectionReady("About")

  const triggerRef = useRef<HTMLDivElement | null>(null)
  const targetRef = useRef<HTMLDivElement | null>(null)

  const lenisAboutRef = useRef<Lenis | null>(null)
  const lenisWrapperRef = useRef<HTMLDivElement | null>(null)
  const lenisContentRef = useRef<HTMLDivElement | null>(null)

  const initAboutLenis = useCallback(() => {
    const experience = Experience.getInstance()
    experience.lenis?.stop()

    lenisAboutRef.current = new Lenis({
      wrapper: lenisWrapperRef.current ?? undefined,
      content: lenisContentRef.current ?? undefined,
    })

    // Add Lenis's requestAnimationFrame (raf) method to GSAP's ticker
    gsap.ticker.add((time: number) => {
      lenisAboutRef.current?.raf(time * 1000)
    })

    // Disable lag smoothing in GSAP to prevent any delay in scroll animations
    gsap.ticker.lagSmoothing(0)
  }, [])

  const destroyAboutLenis = () => {
    lenisAboutRef.current?.destroy()
  }

  const backClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const experience = Experience.getInstance()

      experience.trigger(EVENTS.About.LeaveCTA.Click, [
        {
          target: e.currentTarget,
          htmlElement: e.currentTarget,
        },
      ])
    },
    [],
  )

  useEffect(() => {
    if (!isSectionReady) {
      return
    }

    const experience = Experience.getInstance()
    experience.trigger(EVENTS.About.Entered, [
      {
        target: targetRef.current,
        trigger: triggerRef.current,
      },
    ])

    if (experience.lenis) {
      initAboutLenis()
    } else {
      experience.on(EVENTS.Lenis.Initialized, initAboutLenis)
    }

    return () => {
      destroyAboutLenis()
      experience.trigger(EVENTS.About.Exiting)

      setTimeout(() => {
        experience.lenis?.resize()
      }, 500)
    }
  }, [isSectionReady, initAboutLenis])

  useGSAP(() => {
    const trigger = triggerRef.current
    const target = targetRef.current

    if (!trigger || !target) {
      return
    }

    const experience = Experience.getInstance()
    const screenWidth = document.body.clientWidth

    gsap.to(target, {
      x: () => {
        const normalScroll = -(target.scrollWidth - screenWidth)
        const reverseScroll = normalScroll * -1
        return `${reverseScroll}px`
      },
      ease: "none",
      scrollTrigger: {
        trigger,
        start: "top top",
        end: () => `+=${target.offsetWidth + screenWidth}`,
        pin: true,
        scrub: true,
        markers: false,
        scroller: lenisWrapperRef.current ?? undefined,
      },
      onUpdate() {
        experience.trigger(EVENTS.About.Scroll, [
          {
            progress: this.progress(),
            target,
            trigger,
          },
        ])
      },
    })
  })

  return (
    <>
      <div
        data-name="about-text"
        className="fixed w-full z-100 top-15 md:top-20"
      >
        <Grid>
          <div className="col-start-1 md:col-start-5 lg:col-start-9 col-span-4 md:col-span-4 max-w-sm">
            <AnimatedText
              data-font-bold
              className="text-gray-500"
              preventUpdate
              content={loader.about1}
            />
          </div>
          <div className="col-start-1 md:col-start-5 lg:col-start-9 col-span-4 md:col-span-4 max-w-sm">
            <AnimatedText
              className="text-gray-500"
              preventUpdate
              content={loader.about2}
            />
          </div>
        </Grid>
      </div>
      <div data-name="about">
        <SmallButton
          id="back-to-loader-cta"
          className="absolute top-2 left-2 lg:top-5 lg:left-5 z-11 w-15 lg:hidden"
          onClick={backClickHandler}
          hoverEvent={EVENTS.About.LeaveCTA.Hover}
          outEvent={EVENTS.About.LeaveCTA.Out}
        >
          Back
        </SmallButton>

        <div
          ref={lenisWrapperRef}
          className="absolute h-screen w-screen z-10 top-0 left-0 overflow-x-hidden text-white"
        >
          <div ref={lenisContentRef}>
            <div ref={triggerRef} data-trigger className="h-screen relative">
              <div
                ref={targetRef}
                data-target
                className="h-full absolute top-0 left-auto right-[calc(75vw-240px)] lg:right-[calc(50vw-240px)] lg:pl-[75vw] flex flex-col justify-stert lg:justify-end"
              >
                <div className="flex flex-col gap-10 mt-40 lg:my-30">
                  <div className="flex gap-10 items-center flex-start">
                    {loader.about.map((item) => (
                      <div
                        key={item.year}
                        className="shrink-0 w-30 text-gray-700"
                        // style={{ width: `${item.className}vw` }}
                      >
                        <h1
                          data-animation="webgl-text"
                          data-animation-prevent-update
                        >
                          {item.year}
                        </h1>
                        <p
                          className="mt-4 text-gray-500"
                          data-animation="webgl-text"
                          data-animation-prevent-update
                        >
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div
                    data-animation="webgl-timeline"
                    className="h-4 bg-white mr-[240px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        data-name="about-timeline-caption"
        className="fixed bottom-5 lg:bottom-10 text-center w-full"
      >
        <AnimatedText
          className="text-gray-800"
          preventUpdate
          content="Scroll to move the timeline"
        />
      </div>
    </>
  )
}
